import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldAlert, Wrench, Timer, PenTool, Factory, ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal, Tag } from "../components/Reveal";
import { SectionHead, PrimaryBtn, GhostBtn } from "../components/ui";
import { Marquee } from "../components/Marquee";
import { CTABand } from "../components/CTABand";
import { BeforeAfter } from "../components/BeforeAfter";
import { useTitle } from "../components/PageHero";
import { IMG, waLink } from "../data/site";
import { SOLUTIONS } from "../data/solutions";
import { INDUSTRIES } from "../data/industries";
import { PROJECTS } from "../data/projects";
import { INSIGHTS } from "../data/insights";

const PROBLEMS = [
  { icon: ShieldAlert, title: "Excessive Wear", text: "Protect high-wear components with Hardox, CCO and engineered wear solutions." },
  { icon: Wrench, title: "Equipment Damage", text: "Repair, rebuild and recondition components before replacement becomes necessary." },
  { icon: Timer, title: "Unplanned Downtime", text: "Support maintenance teams with rapid fabrication and repair solutions." },
  { icon: PenTool, title: "Custom Fabrication", text: "Manufacture components according to drawings, dimensions and site requirements." },
  { icon: Factory, title: "Plant Maintenance", text: "Support planned shutdowns, repairs and reliability improvement programs." },
];

const MANIFESTO = [
  { n: "01", title: "Engineering Before Steel", text: "Every job begins with the equipment, the operating conditions and the failure mechanism — not with a cutting list." },
  { n: "02", title: "Wear Is a Calculation", text: "Hardox, CCO or hardfacing is chosen against the actual wear mechanism, so protection lasts until your planned stop — not until its own." },
  { n: "03", title: "Repair Before Replace", text: "Where a component's core is sound, we recondition it: crack repair, dimensional restoration, hardfacing. Back to work, not back to order." },
  { n: "04", title: "Saudi-Based Execution", text: "Fabrication and support from inside the Kingdom — site measurement, maintenance-window turnaround, Kingdom-wide project support." },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section ref={ref} className="noise relative flex min-h-[100svh] items-end overflow-hidden pt-[72px]" data-testid="hero-section">
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <img src={IMG.hero} alt="Heavy industrial fabrication" className="h-[115%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/40" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 45% at 75% 60%, rgba(26,93,171,0.14), transparent 70%)" }} />
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-4 pb-20 sm:px-8 lg:px-16">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.6 }}>
          <Tag>Riyadh · Kingdom of Saudi Arabia</Tag>
        </motion.div>
        <h1 className="mt-6 font-display text-6xl font-black uppercase leading-[0.88] tracking-tight text-frost sm:text-8xl lg:text-[7.5rem]" data-testid="hero-headline">
          <MaskedLine delay={0.35}>Engineered to</MaskedLine>
          <MaskedLine delay={0.5}>Keep <span className="text-outline">Saudi</span></MaskedLine>
          <MaskedLine delay={0.65}><span className="font-brand text-gold">Industry</span> Moving.</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-cadmium sm:text-lg"
        >
          Industrial fabrication, wear protection, equipment reconditioning and engineered solutions for demanding operations across the Kingdom.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <PrimaryBtn to="/rfq" testid="hero-rfq-btn">Request a Technical Quote</PrimaryBtn>
          <GhostBtn to="/solutions" testid="hero-explore-btn">Explore Our Solutions</GhostBtn>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-seam/70 pt-6"
        >
          {["Saudi-Based Execution", "Kingdom-Wide Project Support", "Engineering-Led Approach", "Heavy-Duty Capability"].map((t) => (
            <span key={t} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-cadmium">
              <span className="h-1.5 w-1.5 rotate-45 bg-spark" /> {t}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const ProblemSection = () => (
  <section className="steel-grid border-b border-seam py-24 lg:py-32" data-testid="problem-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHead index="01" eyebrow="The Problem" title="When Equipment Wear Becomes Production Risk, SABAKT Steps In."
        text="Plants don't lose money to wear — they lose it to unplanned response. These are the five situations our customers call us into." />
      <div className="mt-14 grid gap-px border border-seam bg-seam sm:grid-cols-2 lg:grid-cols-5">
        {PROBLEMS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08} className="h-full">
            <div className="group flex h-full flex-col bg-obsidian p-7 transition-colors duration-300 hover:bg-steel" data-testid={`problem-card-${i}`}>
              <p.icon size={26} className="text-spark" />
              <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-frost">{p.title}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-cadmium">{p.text}</p>
              <span className="mt-5 h-px w-8 bg-seam transition-all duration-300 group-hover:w-full group-hover:bg-spark" />
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10">
        <GhostBtn to="/contact" testid="problem-cta">Tell Us What You're Dealing With</GhostBtn>
      </Reveal>
    </div>
  </section>
);

const SolutionsFeature = () => {
  const featured = [SOLUTIONS[0], SOLUTIONS[1]];
  return (
    <section className="border-b border-seam py-24 lg:py-32" data-testid="solutions-feature">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHead index="02" eyebrow="Core Solutions" title="Fabrication & Wear Protection — The Two Things Your Plant Runs On." />
        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          {featured.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.12}>
              <Link to={`/solutions/${s.slug}`} data-testid={`solution-feature-${s.slug}`}
                className="group relative block overflow-hidden border border-seam transition-colors duration-300 hover:border-spark">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={s.image} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="bg-iron p-8">
                  <Tag>{`Solution 0${i + 1}`}</Tag>
                  <h3 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-frost">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-cadmium">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-spark">
                    {s.cta} <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 grid gap-px border border-seam bg-seam sm:grid-cols-2 lg:grid-cols-5">
          {SOLUTIONS.slice(2).map((s) => (
            <Link key={s.slug} to={`/solutions/${s.slug}`} data-testid={`solution-tile-${s.slug}`}
              className="group flex items-center justify-between gap-3 bg-obsidian px-6 py-5 transition-colors duration-200 hover:bg-steel">
              <span className="font-display text-base font-bold uppercase tracking-wide text-frost/90 group-hover:text-spark">{s.title}</span>
              <ArrowUpRight size={16} className="shrink-0 text-cadmium group-hover:text-spark" />
            </Link>
          ))}
        </Reveal>
      </div>
    </section>
  );
};

const ReconditioningSection = () => {
  const bucket = SOLUTIONS.find((s) => s.slug === "equipment-reconditioning");
  return (
    <section className="border-b border-seam bg-iron py-24 lg:py-32" data-testid="reconditioning-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-2 lg:px-16">
        <div>
          <SectionHead index="03" eyebrow="Equipment Reconditioning" title="Repair It. Rebuild It. Put It Back to Work."
            text="Replacing heavy industrial components isn't always the best solution. Drag the slider — this is what engineered reconditioning means." />
          <Reveal className="mt-8 border border-seam bg-obsidian p-7">
            <Tag>High-Conversion Service</Tag>
            <h3 className="mt-3 font-display text-2xl font-extrabold uppercase text-frost">{bucket.featured.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-cadmium">{bucket.featured.text}</p>
            <div className="mt-6">
              <PrimaryBtn to="/rfq" testid="bucket-inspection-btn">Request Bucket Inspection</PrimaryBtn>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <BeforeAfter before="/images/cement/c04.jpg" after="/images/cement/c18.jpg" />
          <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-cadmium/60">
            Actual SABAKT workshop photographs — worn crusher component vs hardfaced bucket rebuild.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const TanksStrip = () => (
  <section className="relative overflow-hidden border-b border-seam" data-testid="tanks-strip">
    <img src="/images/steel/s54.jpg" alt="SABAKT-fabricated water storage tanks" className="absolute inset-0 h-full w-full object-cover opacity-20" />
    <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/85 to-obsidian/40" />
    <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
      <Reveal className="max-w-2xl">
        <Tag>04 · Industrial Tanks</Tag>
        <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-frost sm:text-5xl">
          Diesel & Water Storage Tanks, Built to Your Site.
        </h2>
        <p className="mt-5 text-base leading-relaxed text-cadmium sm:text-lg">
          Custom steel tanks fabricated to project requirements — capacity, dimensions, nozzles, supports, ladders and platforms. Designed and fabricated according to applicable project specifications and required standards.
        </p>
        <div className="mt-8">
          <PrimaryBtn to="/solutions/storage-tanks" testid="tanks-quote-btn">Request Tank Fabrication Quote</PrimaryBtn>
        </div>
      </Reveal>
    </div>
  </section>
);

const IndustriesGrid = () => (
  <section className="border-b border-seam py-24 lg:py-32" data-testid="industries-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHead index="05" eyebrow="Industries We Serve" title="Built for the Kingdom's Heaviest Industries." />
      <div className="mt-14 grid gap-px border border-seam bg-seam sm:grid-cols-2 lg:grid-cols-5">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.slug} delay={(i % 5) * 0.06}>
            <Link to={`/industries/${ind.slug}`} data-testid={`industry-card-${ind.slug}`}
              className="group relative block h-44 overflow-hidden bg-obsidian">
              <img src={ind.image} alt={ind.name} className="absolute inset-0 h-full w-full object-cover opacity-40 transition-all duration-500 group-hover:scale-105 group-hover:opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-transparent" />
              <div className="absolute bottom-0 p-5">
                <span className="font-mono text-[10px] text-spark">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-lg font-bold uppercase tracking-wide text-frost group-hover:text-spark">{ind.name}</h3>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Manifesto = () => (
  <section className="steel-grid border-b border-seam py-24 lg:py-32" data-testid="manifesto-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHead index="07" eyebrow="Why SABAKT" title="The SABAKT Operating Standard." />
      <div className="mt-14">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.n} delay={i * 0.06}>
            <div className="group grid items-baseline gap-4 border-t border-seam py-8 transition-colors duration-300 last:border-b hover:bg-iron/50 md:grid-cols-[120px_1fr_1.4fr] md:gap-10" data-testid={`manifesto-${m.n}`}>
              <span className="font-display text-6xl font-black text-seam transition-colors duration-300 group-hover:text-spark md:text-7xl">{m.n}</span>
              <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-frost md:text-3xl">{m.title}</h3>
              <p className="text-base leading-relaxed text-cadmium">{m.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ProjectsTeaser = () => (
  <section className="border-b border-seam bg-iron py-24 lg:py-32" data-testid="projects-teaser">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHead index="08" eyebrow="Projects" title="Proof, Not Promises."
        text="Real workshop photographs and project scopes across reconditioning, wear protection and fabrication — client references available on request." />
      <div className="mt-14 grid gap-8 md:grid-cols-3">
        {PROJECTS.slice(0, 3).map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.1}>
            <Link to="/projects" data-testid={`project-teaser-${p.slug}`} className="group block border border-seam bg-obsidian transition-colors duration-300 hover:border-spark">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <Tag>{p.industry} · {p.location}</Tag>
                <h3 className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-frost group-hover:text-spark">{p.title}</h3>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10">
        <GhostBtn to="/projects" testid="projects-view-all">View All Projects</GhostBtn>
      </Reveal>
    </div>
  </section>
);

const InsightsTeaser = () => (
  <section className="border-b border-seam py-24 lg:py-32" data-testid="insights-teaser">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHead index="09" eyebrow="Industrial Knowledge Center" title="Answers Before You Even Ask." />
      <div className="mt-14 grid gap-px border border-seam bg-seam md:grid-cols-3">
        {INSIGHTS.slice(0, 3).map((a, i) => (
          <Reveal key={a.slug} delay={i * 0.08} className="h-full">
            <Link to={`/insights/${a.slug}`} data-testid={`insight-teaser-${a.slug}`} className="group flex h-full flex-col bg-obsidian p-7 transition-colors duration-300 hover:bg-steel">
              <Tag>{a.category} · {a.minutes} min</Tag>
              <h3 className="mt-3 flex-1 font-display text-xl font-bold uppercase leading-snug tracking-wide text-frost group-hover:text-spark">{a.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-cadmium">{a.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-spark">
                Read <ArrowUpRight size={14} />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const GALLERY = [
  { src: "/images/cement/c19.jpg", label: "Bucket Hardfacing — Quarry Fleet" },
  { src: "/images/cement/c09.jpg", label: "Hardfaced Wear Plates" },
  { src: "/images/concrete/b09.jpg", label: "Mixer Arms — Batching Plants" },
  { src: "/images/steel/s53.jpg", label: "Diesel Storage Tank" },
  { src: "/images/steel/s16.jpg", label: "Structural Frame Welding" },
  { src: "/images/concrete/b29.jpg", label: "Mixer Bottom Liners" },
  { src: "/images/cement/c10.jpg", label: "Impeller Fan Reconditioning" },
  { src: "/images/steel/s26.jpg", label: "Access Platform & Stairs" },
];

const WorkshopGallery = () => (
  <section className="border-b border-seam bg-iron py-24 lg:py-32" data-testid="workshop-gallery">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHead index="06" eyebrow="From the Workshop Floor" title="Real Work. Real Steel. Real Photographs."
        text="No stock imagery — every frame below is from SABAKT's own workshop and project sites in Saudi Arabia." />
      <div className="mt-14 grid gap-px border border-seam bg-seam sm:grid-cols-2 lg:grid-cols-4">
        {GALLERY.map((g, i) => (
          <Reveal key={g.src} delay={(i % 4) * 0.06}>
            <figure className="group relative aspect-[4/3] overflow-hidden bg-obsidian" data-testid={`gallery-${i}`}>
              <img src={g.src} alt={g.label} loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <figcaption className="absolute inset-x-0 bottom-0 translate-y-2 bg-obsidian/85 px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-frost opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                {g.label}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  useTitle("SABAKT — Industrial Engineering, Fabrication & Wear Protection | Saudi Arabia");
  return (
    <main>
      <Hero />
      <Marquee />
      <ProblemSection />
      <SolutionsFeature />
      <ReconditioningSection />
      <TanksStrip />
      <IndustriesGrid />
      <WorkshopGallery />
      <Manifesto />
      <ProjectsTeaser />
      <InsightsTeaser />
      <CTABand title="Send Us the Problem. We'll Engineer the Solution."
        text="RFQs answered by engineers, not sales scripts. Upload drawings, photos or a BOQ — or start the conversation on WhatsApp." />
    </main>
  );
}
